/**
 * The onboarding scheduler (FS-01 §5.2) — the same `Scheduler` contract as `ChartScheduler`, driven
 * by an authored cue list instead of a weighted draw. `Game.loadSegment` swaps the instance and
 * `Game.step` is untouched: onboarding is content, not a code path (FS-08 R07, FS-09 R02).
 *
 * Two rules meet here and nowhere else in the codebase:
 *  - **FS-01 R44** — `T_AFK_SURVIVE` (18 s) governs this segment. Everywhere else Design Bible
 *    §7.1's `MAX_SAFE_DWELL` (4 s) applies and a stationary player must die. Onboarding is where a
 *    new player is allowed to be still, and it is bounded to 18 s of a 90-second script.
 *  - **FS-01 R08** — the first `phantomWindowSeconds` are non-lethal, and the transition to lethal
 *    is global and announced (§5.2 bar 9.1, the filter sweep).
 *
 * Everything the script asks for goes through `configureParams`, so the node guarantee, the banded
 * node mask and the polarity plumbing have exactly one implementation (Pillar P3, invariant 4).
 */
import { PLAY_RADIUS, SPAWN_Z, TUNNEL_RADIUS } from '../core/constants';
import { clamp, wrap2pi } from '../core/math-det';
import type { EventBus } from '../core/events';
import type { EventMap, HazardKind } from '../core/event-map';
import type { Rng } from '../core/rng';
import type { AudioClock } from '../audio/clock';
import type { Director } from './director';
import { configureParams, type Scheduler, type SpawnRequest } from './chart-scheduler';
import { HAZARD_KINDS } from './hazards/kinds';
import type { HazardParams } from './hazards/types';
import {
  ONBOARD_TOTAL,
  TELEGRAPH_DEVICES,
  beatPositionToSeconds,
  type OnboardingCue,
  type OnboardingScript,
} from '../content/onboarding';

const _params: HazardParams = {
  amp: 0,
  freq: 0,
  phase: 0,
  nodeTheta: 0,
  nodeHalfTheta: 0,
  nodeRMin: 0,
  nodeRMax: PLAY_RADIUS,
  polarity: 'rim',
  reach: 1,
  count: 0,
  rate: 0,
  seed: 0,
};

/** Per-cue bookkeeping. Sized once at `reset`, so `update` never allocates (invariant 8). */
interface CueState {
  telegraphed: boolean;
  arrived: boolean;
}

export class OnboardingScheduler implements Scheduler {
  lastNodeTheta = 0;
  lastNodeHalfTheta = 0.2;

  private state: CueState[] = [];
  private lethalAnnounced = false;
  private firstLethalT = 0;
  private ended = false;
  /** Resonance at the moment the conditional Overdrive Core is decided (§5.2 bar 29.1). */
  private resonanceNow = 0;

  constructor(
    private script: OnboardingScript,
    private rng: Rng,
    private clock: AudioClock,
    private bus: EventBus<EventMap>,
  ) {
    this.firstLethalT = beatPositionToSeconds(script.firstLethalAt, script.bpm);
    this.reset();
  }

  /**
   * The onboarding sector's allow-list is checked at author time by
   * `tests/onboarding-script.test.ts` ("uses only kinds sector 1 has introduced"), not at runtime:
   * a scripted cue that names a disallowed kind is a content bug to fail on, never a spawn to drop
   * silently mid-teach.
   */
  setAllowList(_kinds: readonly HazardKind[]): void {}

  /** The script chooses polarity per cue, so the movement mix does not apply here (FS-08 R34). */
  setPolarityMix(_mix: number): void {}

  reset(): void {
    this.state = this.script.cues.map(() => ({ telegraphed: false, arrived: false }));
    this.lethalAnnounced = false;
    this.ended = false;
  }

  get done(): boolean {
    return this.ended;
  }

  /** `Game` feeds the live meter in so the bar-29.1 Overdrive guarantee can read it (§5.2). */
  setResonance(value: number): void {
    this.resonanceNow = value;
  }

  update(director: Director, emit: (req: SpawnRequest) => void): void {
    const now = this.clock.time;
    const leadTime = Math.abs(SPAWN_Z) / Math.max(director.scrollSpeed, 1);

    // The lethal transition is global and one-shot: "solid fill replaces hollow outline across all
    // hazards", announced by a filter sweep in the music (§5.2 bar 9.1).
    if (!this.lethalAnnounced && now >= this.firstLethalT) {
      this.lethalAnnounced = true;
      this.bus.emit('onboarding:lethal', { tSeconds: now });
    }

    for (let i = 0; i < this.script.cues.length; i++) {
      const cue = this.script.cues[i]!;
      const st = this.state[i]!;

      // Telegraph: the hazard becomes visible one lead time before it arrives, and the devices that
      // warn fire with it.
      if (!st.telegraphed && now >= cue.tSeconds - leadTime) {
        st.telegraphed = true;
        this.emitCue(i, cue, 'telegraph');
        if (cue.spawn) this.spawnFor(cue, director, emit);
      }

      // Arrival: the devices that reward, and the HUD reveal (R13 — at the moment the mechanic
      // becomes relevant, not before).
      if (st.telegraphed && !st.arrived && now >= cue.tSeconds) {
        st.arrived = true;
        this.emitCue(i, cue, 'arrive');
        if (cue.revealHud) this.bus.emit('onboarding:reveal', { element: cue.revealHud });
      }

      // Cues are sorted, so the first one that is not yet due ends the sweep.
      if (!st.telegraphed) break;
    }

    if (!this.ended && now >= ONBOARD_TOTAL) {
      this.ended = true;
      this.bus.emit('onboarding:end', { reason: 'completed', tSeconds: now });
    }
  }

  private emitCue(index: number, cue: OnboardingCue, phase: 'telegraph' | 'arrive'): void {
    // Split the cue's devices by when they mean something. A device that warns after the hazard has
    // already passed has taught nothing.
    const wantTelegraph = phase === 'telegraph';
    let devices: readonly (typeof cue.devices)[number][] = cue.devices;
    devices = cue.devices.filter((d) => TELEGRAPH_DEVICES.includes(d) === wantTelegraph);
    if (devices.length === 0 && phase === 'telegraph' && !cue.spawn) return;
    this.bus.emit('onboarding:cue', {
      index,
      mechanic: cue.mechanic,
      devices,
      phase,
      // A phantom is cleared by definition; a lethal cue the player is still alive for was cleared.
      clearedFirstTry: true,
    });
  }

  private spawnFor(cue: OnboardingCue, director: Director, emit: (req: SpawnRequest) => void): void {
    const s = cue.spawn!;
    const def = HAZARD_KINDS[s.kind];

    // §5.2 authors node position as a **lateral offset in u**, from a 2D reading of the playfield.
    // In a radial tunnel the faithful reading is arc length at the play radius: it preserves the
    // table's magnitudes and directions (+3.5 u right, −4.5 u left) and stays a reachable angular
    // step at every radius the ship can occupy.
    const theta = wrap2pi(s.polarity === 'axis' ? 0 : cue.nodeOffsetU / PLAY_RADIUS);

    configureParams(def, director.intensity, theta, this.rng, this.clock, _params, s.polarity);
    _params.reach = s.reach;

    // The script's node width is a **floor raised**, never a ceiling lowered: the guarantee is a
    // minimum and content may only widen it (FS-06 R10, invariant 4). `configureParams` has already
    // derived a width that satisfies MIN_NODE_WIDTH; a wider scripted teach wins, a narrower one
    // is ignored.
    const passableR = Math.max(TUNNEL_RADIUS - _params.amp, 2.5);
    const scriptedHalf = clamp(cue.nodeWidthU / (2 * passableR), 0.14, 0.7);
    _params.nodeHalfTheta = Math.max(_params.nodeHalfTheta, scriptedHalf);

    this.lastNodeTheta = _params.nodeTheta;
    this.lastNodeHalfTheta = _params.nodeHalfTheta;

    // §5.2 bar 29.1 — the Core is placed only for a player who has not built Resonance on their
    // own, so that everyone sees Overdrive inside the first 90 s without it being handed to someone
    // who was already going to get there.
    const pickup =
      s.pickup && (s.pickupIfResonanceBelow === undefined || this.resonanceNow < s.pickupIfResonanceBelow)
        ? s.pickup
        : undefined;

    emit({
      def,
      params: _params,
      z: SPAWN_Z,
      targetBeat: 0,
      intensity: director.intensity,
      lethal: !cue.phantom,
      pickup,
    });
  }

  /** Ends the script early (R15's skip, or a death). `Game` decides which reason applies. */
  end(reason: 'skipped' | 'quit' | 'died'): void {
    if (this.ended) return;
    this.ended = true;
    this.bus.emit('onboarding:end', { reason, tSeconds: this.clock.time });
  }
}
